/**
 * ตัวห่อ stockSyncEngine สำหรับแอป (local mode เท่านั้น)
 *
 * - engine 1 ตัวต่อร้าน ใช้ร่วมกันทุกหน้า แต่ละร้านใช้ database ของตัวเอง (ดู stockTxDb.ts)
 * - ต่อ api จริง (/inventories-v2/*) และ IndexedDB จริง
 * - logout / สลับบัญชีร้าน: ไม่ลบแคช กลับมาร้านเดิมใช้ต่อได้เลย (sync แค่ส่วนที่เปลี่ยน)
 * - wipeAllStockCaches(): ปิดทุก engine แล้วลบ database ของทุกร้าน — ใช้ตอนเห็น flag
 *   stock_checkpoint ไม่เป็น true เท่านั้น (checkpoint ในเครื่องเชื่อไม่ได้แล้ว)
 * - ร้านที่ไม่ได้เปิดเกิน 7 วัน (แคชหมดอายุ ยังไงก็ต้องโหลดใหม่) ลบ database ทิ้งให้ เครื่องจะได้ไม่บวม
 */

import dataservice from "../../core/services/dataservice";
import { createStockEngine, type StockApi, type StockEngine } from "./stockSyncEngine";
import { deleteStockDb, getStockDb, listStockDbStoreIds } from "./stockTxDb";
import { MAX_CACHE_AGE_MS, type StockRow } from "./stockCheckpoint";

// ร้านที่มีแคชในเครื่อง { storeId: เวลาที่ใช้ล่าสุด }
// ไม่มี = เครื่องนี้ไม่เคยใช้ local mode -> ตอนล้างไม่ต้องแตะ IndexedDB เลย
const REGISTRY_KEY = "stockCache:stores";
const TOUCH_EVERY_MS = 60 * 1000;

const safeLocalStorage = {
  get(key: string) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
    } catch {}
  },
  remove(key: string) {
    try {
      localStorage.removeItem(key);
    } catch {}
  },
};

const readRegistry = (): Record<string, number> => {
  try {
    const parsed = JSON.parse(safeLocalStorage.get(REGISTRY_KEY) || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeRegistry = (registry: Record<string, number>) => {
  if (Object.keys(registry).length) safeLocalStorage.set(REGISTRY_KEY, JSON.stringify(registry));
  else safeLocalStorage.remove(REGISTRY_KEY);
};

const lastTouch = new Map<string, number>();
/** จดว่าร้านนี้เพิ่งใช้แคช (ไม่ถี่กว่านาทีละครั้ง) */
const touch = (storeId: string, now = Date.now()) => {
  if (now - (lastTouch.get(storeId) || 0) < TOUCH_EVERY_MS) return;
  lastTouch.set(storeId, now);
  writeRegistry({ ...readRegistry(), [storeId]: now });
};

/** /inventories-v2/* ตัวจริง (แท็บ Verify ใช้ร่วมกับ engine ในหน่วยความจำ) */
export const stockApi: StockApi = {
  async sync(storeId, checkpoint) {
    const res = await dataservice.invetoryTransaction.syncStock(storeId, checkpoint);
    return res.data;
  },
  async fetchAllPage(storeId, cursor) {
    const res = await dataservice.invetoryTransaction.fetchStockAllPage(storeId, cursor);
    const body = res.data || {};
    return { items: body.items || [], nextCursor: body.nextCursor || null };
  },
};

interface Handle {
  engine: StockEngine;
  listeners: Set<(rows: StockRow[]) => void>;
}

const handles = new Map<string, Promise<Handle>>();
// การลบแคชที่ยังไม่เสร็จ — engine ใหม่ต้องรอให้เสร็จก่อน ไม่งั้นโดนลบทับกลางทาง
let pendingWipe: Promise<void> | null = null;
let prunedThisSession = false;

/** engine ของร้านนี้ (สร้างครั้งแรกที่ขอ) — ใช้ database ของร้านนี้เท่านั้น */
export const getStockHandle = (storeId: string): Promise<Handle> => {
  if (!storeId) return Promise.reject(new Error("stock: ไม่มี storeId"));
  touch(storeId);

  let existing = handles.get(storeId);
  if (!existing) {
    existing = Promise.resolve(pendingWipe)
      .then(() => getStockDb(storeId))
      .then((db) => {
        const listeners = new Set<(rows: StockRow[]) => void>();
        const engine = createStockEngine({
          storeId,
          api: stockApi,
          db,
          onChange: (rows) => listeners.forEach((fn) => fn(rows)),
        });
        return { engine, listeners };
      });
    handles.set(storeId, existing);
  }

  if (!prunedThisSession) {
    prunedThisSession = true;
    existing.then(() => pruneStaleStockCaches()).catch(() => {});
  }
  return existing;
};

/**
 * ลบ database ของร้านที่ไม่ได้ใช้เกิน 7 วัน (แคชหมดอายุแล้ว เปิดมาก็ต้องโหลดเต็มอยู่ดี — ลบไม่ทำให้ยิงเพิ่ม)
 * ข้ามร้านที่มี engine เปิดอยู่ในแท็บนี้
 */
export const pruneStaleStockCaches = async (now = Date.now(), maxAgeMs = MAX_CACHE_AGE_MS): Promise<string[]> => {
  const removed: string[] = [];
  for (const [storeId, usedAt] of Object.entries(readRegistry())) {
    if (handles.has(storeId)) continue;
    if (!(now - Number(usedAt || 0) > maxAgeMs)) continue;
    await deleteStockDb(storeId);
    const registry = readRegistry();
    delete registry[storeId];
    writeRegistry(registry);
    lastTouch.delete(storeId);
    removed.push(storeId);
  }
  return removed;
};

/** ร้านที่มีแคชอยู่ในเครื่อง (ตามที่จดไว้) */
export const cachedStockStoreIds = (): string[] => Object.keys(readRegistry());

/**
 * ปิดทุก engine แล้วลบ database แคชสต๊อกของทุกร้านในเครื่อง
 * ใช้ตอน flag stock_checkpoint ไม่เป็น true เท่านั้น — logout / สลับร้าน ไม่ลบ
 */
export const wipeAllStockCaches = (): Promise<void> => {
  const pending = [...handles.values()];
  handles.clear();

  const previous = pendingWipe;
  const run = (async () => {
    await previous;
    for (const handle of pending) {
      try {
        (await handle).engine.dispose();
      } catch {}
    }

    const known = Object.keys(readRegistry());
    if (!known.length) return;
    try {
      const listed = await listStockDbStoreIds();
      for (const storeId of new Set([...known, ...listed])) await deleteStockDb(storeId);
      writeRegistry({});
      lastTouch.clear();
    } catch (error) {
      console.error("[stock] ลบแคชสต๊อกไม่สำเร็จ", error);
    }
  })();

  pendingWipe = run;
  run.finally(() => {
    if (pendingWipe === run) pendingWipe = null;
  });
  return run;
};
